import { randomUUID } from 'expo-crypto';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import {
  SpeechCaptureApi,
  buildCaptureHtml,
  decodeCaptureMessage,
} from './capturePage';
import {
  SpeechCaptureHandle,
  SpeechCaptureProps,
  captureToken,
} from './speechCaptureTypes';

/**
 * The web: the same capture page in a hidden same-origin iframe, so one
 * capture runs everywhere.
 */
export const SpeechCapture = forwardRef<
  SpeechCaptureHandle,
  SpeechCaptureProps
>(({ endpoint, onMessage }, ref) => {
  const frame = useRef<HTMLIFrameElement>(null);
  const session = useMemo(() => randomUUID(), []);
  const { socket, frames, clip, keepMicrophoneMs } = endpoint;
  const html = useMemo(() => {
    try {
      return buildCaptureHtml(
        { socket, frames, clip, keepMicrophoneMs },
        session,
      );
    } catch (error) {
      // Without a page there is no voice; typing still works.
      console.error('speech capture unavailable', error);
      return null;
    }
  }, [session, socket, frames, clip, keepMicrophoneMs]);
  // The page's CSP refuses eval: call its API on the frame's window.
  const voice = () =>
    (
      frame.current?.contentWindow as
        | (Window & { speechCapture?: SpeechCaptureApi })
        | null
        | undefined
    )?.speechCapture;
  const handler = useRef(onMessage);
  handler.current = onMessage;
  useEffect(() => {
    // The frame's window proxy outlives its page loads; read it now, since
    // the ref is cleared before this cleanup runs.
    const frameWindow = frame.current?.contentWindow as
      | (Window & { speechCapture?: SpeechCaptureApi })
      | null
      | undefined;
    const receive = (event: MessageEvent) => {
      if (event.source !== frame.current?.contentWindow) return;
      if (typeof event.data !== 'string') return;
      const message = decodeCaptureMessage(event.data, session);
      if (!message) return;
      if (message.kind === 'authorize') {
        void captureToken(message.refresh).then(token => voice()?.auth(token));
        return;
      }
      handler.current(message);
    };
    window.addEventListener('message', receive);
    return () => {
      window.removeEventListener('message', receive);
      frameWindow?.speechCapture?.release();
    };
  }, [session]);
  useImperativeHandle(ref, () => ({
    start: payload => voice()?.start(payload),
    stop: () => voice()?.stop(),
    cancel: () => voice()?.cancel(),
  }));
  if (!html) return null;
  return (
    <iframe
      ref={frame}
      title="speech-capture"
      srcDoc={html}
      allow="microphone"
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        border: 0,
        pointerEvents: 'none',
      }}
    />
  );
});
